import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuthCtx } from "../hooks/AuthContext";

// Less-frequently-used destinations tucked under an "Others" menu.
const OTHERS = [
  { to: "/roasters", label: "Roasters" },
  { to: "/drippers", label: "Drippers" },
  { to: "/grinders", label: "Grinders" },
  { to: "/stats", label: "Stats" },
  { to: "/cafe", label: "Café Log" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const nav = useNavigate();
  const location = useLocation();
  const { me, logout } = useAuthCtx();
  const [open, setOpen] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);       // desktop dropdown
  const [othersOpenM, setOthersOpenM] = useState(false);     // mobile sub-section
  const onLogout = async () => { await logout(); nav("/login"); };
  const isAdmin = !!me?.is_admin;
  const othersActive = OTHERS.some((o) => location.pathname.startsWith(o.to));

  const closeMobile = () => { setOpen(false); setOthersOpenM(false); };

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `text-[11px] uppercase tracking-widest pb-0.5 border-b ${
      isActive ? "border-ink text-ink" : "border-transparent text-paper-500 hover:text-ink"
    }`;
  const mobileLinkCls = ({ isActive }: { isActive: boolean }) =>
    `block py-3 text-sm uppercase tracking-widest border-b border-paper-200 ${
      isActive ? "text-ink font-medium" : "text-paper-600"
    }`;
  const dropItemCls = ({ isActive }: { isActive: boolean }) =>
    `block px-3 py-2 text-[11px] uppercase tracking-widest ${
      isActive ? "text-ink bg-paper-100" : "text-paper-600 hover:text-ink hover:bg-paper-100"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-paper-50">
      <header className="border-b border-paper-200 bg-paper-50 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center gap-4 sm:gap-8">
          <Link to="/" onClick={closeMobile} className="font-display font-medium tracking-tight text-base text-ink">
            Coffee Vault
          </Link>
          {/* Desktop nav */}
          <nav className="hidden md:flex gap-6 ml-2 items-center">
            <NavLink to="/" end className={linkCls}>Index</NavLink>
            <NavLink to="/browse" className={linkCls}>Browse</NavLink>
            <NavLink to="/add" className={linkCls}>Add</NavLink>
            <NavLink to="/labels" className={linkCls}>Labels</NavLink>
            <NavLink to="/stocktake" className={linkCls}>Stocktake</NavLink>
            {isAdmin ? <NavLink to="/admin/users" className={linkCls}>Users</NavLink> : null}
            {/* Others dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setOthersOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={othersOpen}
                className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest pb-0.5 border-b ${
                  othersActive ? "border-ink text-ink" : "border-transparent text-paper-500 hover:text-ink"
                }`}
              >
                Others
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"
                  className={`transition-transform ${othersOpen ? "rotate-180" : ""}`}>
                  <path d="M2 3.5L5 6.5L8 3.5" />
                </svg>
              </button>
              {othersOpen ? (
                <>
                  {/* click-away backdrop */}
                  <div className="fixed inset-0 z-10" onClick={() => setOthersOpen(false)} />
                  <div className="absolute left-0 top-full mt-2 z-20 min-w-[150px] border border-paper-200 bg-paper-50 shadow-md py-1">
                    {OTHERS.map((o) => (
                      <NavLink key={o.to} to={o.to} onClick={() => setOthersOpen(false)} className={dropItemCls}>
                        {o.label}
                      </NavLink>
                    ))}
                    <a href="/flavor-map.html" target="_blank" rel="noreferrer" onClick={() => setOthersOpen(false)}
                      className="block px-3 py-2 text-[11px] uppercase tracking-widest text-paper-600 hover:text-ink hover:bg-paper-100">
                      Flavour Map ↗
                    </a>
                  </div>
                </>
              ) : null}
            </div>
          </nav>
          <div className="hidden md:flex ml-auto items-center gap-4">
            {me?.email ? (
              <Link to="/account" className="text-[11px] text-paper-500 hover:text-ink truncate max-w-[180px]" title={me.email}>
                {me.email}
              </Link>
            ) : null}
            <button onClick={onLogout} className="text-[11px] uppercase tracking-widest text-paper-500 hover:text-ink">
              Sign out
            </button>
          </div>
          {/* Mobile menu toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="md:hidden ml-auto p-1 -mr-1 text-ink"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.5">
              {open ? (
                <><path d="M5 5l12 12" /><path d="M17 5L5 17" /></>
              ) : (
                <><path d="M3 6h16" /><path d="M3 11h16" /><path d="M3 16h16" /></>
              )}
            </svg>
          </button>
        </div>
        {/* Mobile drawer */}
        {open ? (
          <div className="md:hidden border-t border-paper-200 px-4 pb-2">
            <NavLink to="/" end onClick={closeMobile} className={mobileLinkCls}>Index</NavLink>
            <NavLink to="/browse" onClick={closeMobile} className={mobileLinkCls}>Browse</NavLink>
            <NavLink to="/add" onClick={closeMobile} className={mobileLinkCls}>Add</NavLink>
            <NavLink to="/labels" onClick={closeMobile} className={mobileLinkCls}>Labels</NavLink>
            <NavLink to="/stocktake" onClick={closeMobile} className={mobileLinkCls}>Stocktake</NavLink>
            {isAdmin ? <NavLink to="/admin/users" onClick={closeMobile} className={mobileLinkCls}>Users</NavLink> : null}
            {/* Others (collapsible) */}
            <button
              type="button"
              onClick={() => setOthersOpenM((v) => !v)}
              aria-expanded={othersOpenM}
              className={`w-full flex items-center justify-between py-3 text-sm uppercase tracking-widest border-b border-paper-200 ${
                othersActive ? "text-ink font-medium" : "text-paper-600"
              }`}
            >
              Others
              <svg width="11" height="11" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"
                className={`transition-transform ${othersOpenM ? "rotate-180" : ""}`}>
                <path d="M2 3.5L5 6.5L8 3.5" />
              </svg>
            </button>
            {othersOpenM ? (
              <div className="pl-4">
                {OTHERS.map((o) => (
                  <NavLink key={o.to} to={o.to} onClick={closeMobile} className={mobileLinkCls}>
                    {o.label}
                  </NavLink>
                ))}
                <a href="/flavor-map.html" target="_blank" rel="noreferrer" onClick={closeMobile}
                  className="block py-3 text-sm uppercase tracking-widest border-b border-paper-200 text-paper-600">
                  Flavour Map ↗
                </a>
              </div>
            ) : null}
            <NavLink to="/account" onClick={closeMobile} className={mobileLinkCls}>Account</NavLink>
            <button onClick={() => { closeMobile(); onLogout(); }} className="block py-3 text-sm uppercase tracking-widest text-paper-500 w-full text-left">
              Sign out
            </button>
          </div>
        ) : null}
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <footer className="border-t border-paper-200 px-4 sm:px-6 py-6 text-[10px] uppercase tracking-widest text-paper-400 print:hidden">
        Coffee Vault · Personal Index
      </footer>
    </div>
  );
}
